import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { coll } from "@/lib/ddb";
import { useCollection, useProject, useSubscription } from "@/lib/ddb-hooks";
import { CABINET_REGISTRY } from "@/lib/cabinets";
import { indexCatalogs } from "@/lib/cabinets/bom";
import { deriveCabinetCache } from "@/lib/cabinets/validation";
import { deriveProjectTotals } from "@/lib/cabinets/projectTotals";
import {
  addCabinet,
  addCabinets,
  addGarnitur,
  allCabinets,
  cabinetCountsByGarnitur as deriveCabinetCounts,
  moveCabinetToGarnitur,
  removeCabinets,
  removeGarnitur,
  updateCabinet,
  updateCabinetsWhere,
} from "@/lib/cabinets/projectOps";
import type { EdgeBand, Hardware, Material } from "@/lib/types";
import { collisionIds, doorSwingCollisionIds, outOfBoundsIds, wallConflictIds } from "@/lib/geometry";
import type { Cabinet, Garnitur, GarniturType, Id, Project, ProjectStatus } from "@/lib/types";
import { RoomScene } from "@/scene/RoomScene";
import { CabinetPalette } from "@/panels/CabinetPalette";
import { CabinetEditor } from "@/panels/CabinetEditor";
import { GarnitursPanel } from "@/panels/GarnitursPanel";
import { ProjectOutliner } from "@/panels/ProjectOutliner";
import { ProjectToolbar } from "@/panels/ProjectToolbar";
import { DrawerInspector } from "@/panels/DrawerInspector";
import { PartInspector } from "@/panels/PartInspector";
import { PartEditor } from "@/panels/PartEditor";
import { NEXT_STATUS } from "@/components/StatusBadge";

export function ProjectEditorPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const numId = id ? Number(id) : null;

  const { data: project, isLoading: pLoading, error: pError } = useProject(numId);
  const { data: materials } = useCollection<Material>("materials");
  const { data: edgeBands } = useCollection<EdgeBand>("edge_bands");
  const { data: hardware } = useCollection<Hardware>("hardware");
  const catalog = useMemo(() => {
    if (!materials || !edgeBands || !hardware) return null;
    return indexCatalogs(materials, edgeBands, hardware);
  }, [materials, edgeBands, hardware]);
  // Cabinets + garniturs are now sub-objects on `project`, not separate
  // collections. Flatten for legacy consumers (collision math, scene
  // render, BOM); the `garniturList` keeps the tree shape.
  const cabinets = useMemo(() => allCabinets(project), [project]);
  const garniturList = useMemo<Garnitur[]>(() => project?.garniturs ?? [], [project]);

  // Multiselect: Set of cabinet ids.  Single click = replace; shift-click =
  // toggle. PivotControls only shows on a single-cabinet selection.
  const [selectedIds, setSelectedIds] = useState<Set<Id>>(() => new Set());
  const [pbr, setPbr] = useState(false);

  // Side-panel collapse state, persisted across reloads in localStorage.
  // First visit on mobile (<768px viewport) defaults to both collapsed
  // so the 3D scene gets the full screen — user can still expand each
  // panel via the toggle and the choice is remembered.
  const initialCollapsed = (key: string): boolean => {
    const stored = localStorage.getItem(key);
    if (stored != null) return stored === "1";
    return typeof window !== "undefined"
        && window.matchMedia("(max-width: 768px)").matches;
  };
  const [leftCollapsed, setLeftCollapsedState] =
    useState<boolean>(() => initialCollapsed("editor.leftCollapsed"));
  const [rightCollapsed, setRightCollapsedState] =
    useState<boolean>(() => initialCollapsed("editor.rightCollapsed"));
  // Persist on toggle, not via useEffect, so the viewport-derived default
  // isn't written back on first render (otherwise a user who first opened
  // on desktop, then revisits on mobile, would still see panels expanded).
  const setLeftCollapsed = (next: boolean) => {
    setLeftCollapsedState(next);
    localStorage.setItem("editor.leftCollapsed", next ? "1" : "0");
  };
  const setRightCollapsed = (next: boolean) => {
    setRightCollapsedState(next);
    localStorage.setItem("editor.rightCollapsed", next ? "1" : "0");
  };

  // Deep-link: ?selectedId=N (used by ProblemsPage) pre-selects a cabinet
  // on mount and strips the param so a subsequent click doesn't undo it.
  useEffect(() => {
    const raw = searchParams.get("selectedId");
    if (raw == null) return;
    const id = Number(raw);
    if (Number.isFinite(id)) {
      setSelectedIds(new Set([id]));
      const next = new URLSearchParams(searchParams);
      next.delete("selectedId");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /** Which garnitur receives new cabinets from the palette. Falls back to
   *  the first garnitur if the user hasn't picked yet. */
  const [currentGarniturId, setCurrentGarniturId] = useState<Id | null>(null);

  // Page-level PartEditor modal state. Hosted here (not inside
  // CabinetEditor) so it can pop open regardless of which inspector
  // is mounted — Drawer/Part inspectors need to be able to drill into
  // the deep editor too.
  const [editPartCtx, setEditPartCtx] = useState<{ cabinetId: Id; partId?: string } | null>(null);

  // Secondary selection — when set, the right-rail inspector renders
  // a drawer or part inspector instead of the cabinet's. Cleared when
  // the user picks a different (or no) cabinet, or shifts focus back.
  type SecondarySelection =
    | { kind: "drawer"; cabinetId: Id; drawerId: Id }
    | { kind: "part";   cabinetId: Id; partId: string };
  const [secondary, setSecondary] = useState<SecondarySelection | null>(null);

  function handleSelectPart(cabinetId: Id, partId: string) {
    setSelectedIds(new Set([cabinetId]));
    setSecondary({ kind: "part", cabinetId, partId });
  }
  function handleSelectDrawer(cabinetId: Id, drawerId: Id) {
    setSelectedIds(new Set([cabinetId]));
    setSecondary({ kind: "drawer", cabinetId, drawerId });
  }
  /** Open the deep PartEditor modal — called by CabinetEditor's
   *  "Edit parts" button (no part preselected) and by PartInspector's
   *  "Edit details" button (preselects the inspected part). */
  function openPartEditorModal(cabinetId: Id, partId?: string) {
    setEditPartCtx({ cabinetId, partId });
  }

  function handleSelect(id: Id | null, shiftHeld = false) {
    // Picking a different cabinet (or none) drops the secondary
    // drawer/part target — the inspector should follow the user's
    // primary 3D selection.
    setSecondary((s) => {
      if (id == null) return null;
      if (s && s.cabinetId === id && !shiftHeld) return s;
      return null;
    });
    setSelectedIds((prev) => {
      if (id == null) return new Set();
      if (!shiftHeld) return new Set([id]);
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Live updates: WS push on the project itself. One doc, one subscribe.
  useSubscription<Project>(
    "projects",
    numId != null ? [{ field: "_id", op: "eq", value: numId }] : [],
    () => { void qc.invalidateQueries({ queryKey: ["obj", "projects", numId] }); },
    numId != null,
  );

  // Once garniturs are loaded, auto-pick the first one (sorted by id =
  // creation order). Sticky across re-renders. Deletion explicitly nulls
  // currentGarniturId in handleDeleteGarnitur — we never "fall back" on
  // missing ids inside this effect because the React Query cache may be
  // momentarily stale during a refetch after a fresh insert, and an
  // overeager fallback would yank the user's selection back to the first
  // row right after they created a new garnitur.
  useEffect(() => {
    if (currentGarniturId == null && garniturList[0]) {
      setCurrentGarniturId(garniturList[0]._id);
    }
  }, [garniturList, currentGarniturId]);

  const cabinetCountsByGarnitur = useMemo<Record<Id, number>>(
    () => deriveCabinetCounts(project),
    [project],
  );

  /** Persist a project mutation. Recomputes `totals` from the candidate
   *  tree before the wire write so the project card / status badge see
   *  consistent numbers without waiting on a trigger refresh. */
  const persist = useCallback(async (next: Project) => {
    if (!project) return;
    const totals = (materials && edgeBands && hardware)
      ? deriveProjectTotals(next.garniturs ?? [], { materials, edges: edgeBands, hardware })
      : next.totals ?? project.totals;
    await coll("projects").update<Project>(project._id, {
      garniturs: next.garniturs,
      _nextLocalId: next._nextLocalId,
      totals,
      updatedAt: new Date().toISOString(),
    });
    void qc.invalidateQueries({ queryKey: ["obj", "projects", project._id] });
  }, [project, materials, edgeBands, hardware, qc]);

  const handleAddGarnitur = useCallback(async (name: string, type: GarniturType) => {
    if (!project) return;
    const { project: next, garniturId } = addGarnitur(project, { name, type });
    await persist(next);
    setCurrentGarniturId(garniturId);
  }, [project, persist]);

  const handleDeleteGarnitur = useCallback(async (id: Id) => {
    if (!project) return;
    const next = removeGarnitur(project, id);
    await persist(next);
    if (currentGarniturId === id) setCurrentGarniturId(null);
  }, [project, currentGarniturId, persist]);

  // Partition raw cabinet data into ones whose classId resolves against
  // CABINET_REGISTRY (good — render normally) and orphans (bad data —
  // likely from a stale schema or a manual REST insert with a fake
  // classId). The scene, collision checks, and BOM all run only on
  // `cabinetRows`; orphans surface in a banner with a "delete all" action.
  const cabinetRows = useMemo(
    () => cabinets.filter((c) => CABINET_REGISTRY[c.classId] != null),
    [cabinets],
  );
  const orphanCabinets = useMemo(
    () => cabinets.filter((c) => CABINET_REGISTRY[c.classId] == null),
    [cabinets],
  );
  // For single-selection workflows: the sole selected cabinet (or null).
  const selected = useMemo(() => {
    if (selectedIds.size !== 1) return null;
    const onlyId = selectedIds.values().next().value;
    return cabinetRows.find((c) => c._id === onlyId) ?? null;
  }, [cabinetRows, selectedIds]);
  /** Garnitur id of the sole-selected cabinet — needed by CabinetEditor's
   *  "move to garnitur" select. */
  const selectedGarniturId = useMemo<Id | null>(() => {
    if (!selected || !project) return null;
    for (const g of project.garniturs ?? []) {
      if ((g.cabinets ?? []).some((c) => c._id === selected._id)) return g._id;
    }
    return null;
  }, [project, selected]);
  const selectedList = useMemo(
    () => cabinetRows.filter((c) => selectedIds.has(c._id)),
    [cabinetRows, selectedIds],
  );

  /** Move the sole-selected cabinet into another garnitur. CabinetEditor
   *  hands us a target id when the user picks from its dropdown. */
  const handleMoveSelectedToGarnitur = useCallback(async (targetGarniturId: Id) => {
    if (!project || selected == null) return;
    await persist(moveCabinetToGarnitur(project, selected._id, targetGarniturId));
  }, [project, selected, persist]);
  // Two severity tiers: cabinets that physically overlap (cab×cab OR cab×wall)
  // render red; cabinets fully or partly outside the room polygon render
  // yellow. Wall-conflict subsumed into `collisions` so the visual is
  // unified — same threat level.
  const collisions = useMemo(() => {
    const cabXcab = collisionIds(cabinetRows);
    const cabXwall = wallConflictIds(cabinetRows, project?.room?.walls ?? []);
    const merged = new Set(cabXcab);
    for (const id of cabXwall) merged.add(id);
    return merged;
  }, [cabinetRows, project?.room?.walls]);
  const outOfBounds = useMemo(
    () => outOfBoundsIds(cabinetRows, project?.room?.walls ?? []),
    [cabinetRows, project?.room?.walls],
  );
  // Soft warning: cabinets in any door's 90° swing arc.
  const swingBlocks = useMemo(
    () => doorSwingCollisionIds(cabinetRows, project?.room?.walls ?? []),
    [cabinetRows, project?.room?.walls],
  );

  const handleAdd = useCallback(async (classId: string) => {
    if (!project) return;
    const cls = CABINET_REGISTRY[classId];
    if (!cls) return;

    // Lay out new cabinets along the +X wall, spaced by their width.
    // Group floor vs wall anchors separately so uppers can sit above bases
    // rather than pushing each other sideways.
    const peers = cabinetRows.filter((c) => c.anchor === cls.anchor);
    const baseX = peers.reduce((acc, c) => acc + c.width, 0);
    const baseY = cls.anchor === "wall" ? 54 : 0;
    const anchor = cls.anchor === "ceiling" ? "wall" : cls.anchor;

    // Walk forward in +X until the new footprint doesn't overlap any peer.
    // 6" step is finer than typical cabinet width; cap at 240" so a
    // crowded room doesn't loop forever (user sees a red placement and
    // can drag it out).
    let placeX = baseX;
    for (let dx = 0; dx <= 240; dx += 6) {
      const probe: Cabinet = {
        _id: -1 as Id,
        classId, position: [baseX + dx, baseY, 4], rotation: 0,
        anchor,
        width: cls.defaults.w, height: cls.defaults.h, depth: cls.defaults.d,
        overrides: {}, cached: { partCount: 0 } as Cabinet["cached"],
      };
      if (!collisionIds([...cabinetRows, probe]).has(-1 as Id)) {
        placeX = baseX + dx;
        break;
      }
    }

    // Build the destination garnitur and the new cabinet in one project
    // tree, persist once. Avoids the stale-closure trap where calling a
    // separate `ensureGarnitur()` first would invalidate the local
    // `project` reference.
    const draftBase: Omit<Cabinet, "_id" | "cached"> = {
      classId,
      position: [placeX, baseY, 4],
      rotation: 0,
      anchor,
      width:  cls.defaults.w,
      height: cls.defaults.h,
      depth:  cls.defaults.d,
      overrides: {},
    };
    const cached = catalog
      ? (deriveCabinetCache(
          { _id: -1, ...draftBase, cached: { partCount: 0 } as Cabinet["cached"] },
          catalog,
        ) as Cabinet["cached"])
      : ({ partCount: 0 } as Cabinet["cached"]);

    let working = project;
    let targetGarnitur: Id | null = currentGarniturId ?? garniturList[0]?._id ?? null;
    if (targetGarnitur == null) {
      const res = addGarnitur(working, { name: "Main", type: "lower" });
      working = res.project;
      targetGarnitur = res.garniturId;
      setCurrentGarniturId(targetGarnitur);
    }
    const { project: next, cabinetId } = addCabinet(working, targetGarnitur, { ...draftBase, cached });
    await persist(next);
    setSelectedIds(new Set([cabinetId]));
  }, [project, cabinetRows, catalog, currentGarniturId, garniturList, persist]);

  const handleChange = useCallback(async (changes: Partial<Cabinet>) => {
    if (!project || selected == null) return;
    // Clamp dim changes against per-class bounds before sending.
    const cls = CABINET_REGISTRY[selected.classId];
    if (cls && (changes.width != null || changes.height != null || changes.depth != null)) {
      const clamped = cls.clamp({
        w: changes.width  ?? selected.width,
        h: changes.height ?? selected.height,
        d: changes.depth  ?? selected.depth,
      });
      changes = { ...changes, width: clamped.w, height: clamped.h, depth: clamped.d };
    }
    // Reject geometry changes (position / size / rotation) that would make
    // this cabinet overlap another. Other field edits (overrides, etc.)
    // pass through unconditionally.
    const touchesGeom = changes.position != null || changes.rotation != null
                     || changes.width != null || changes.height != null || changes.depth != null;
    if (touchesGeom) {
      const candidate = cabinetRows.map((c) => c._id === selected._id ? { ...c, ...changes } : c);
      if (collisionIds(candidate).has(selected._id)) {
        void qc.invalidateQueries({ queryKey: ["obj", "projects", project._id] });
        return;
      }
    }
    // Stamp cached summary so the cabinet list / cutlist / status badges
    // reflect part-level edits immediately. Skip if catalog isn't loaded
    // yet (initial render); the next save will re-stamp.
    if (catalog) {
      const merged: Cabinet = { ...selected, ...changes };
      changes = {
        ...changes,
        cached: { ...(merged.cached ?? {}), ...deriveCabinetCache(merged, catalog) },
      };
    }
    await persist(updateCabinet(project, selected._id, changes));
  }, [project, selected, cabinetRows, catalog, persist, qc]);

  /** Delete every selected cabinet. */
  const handleDelete = useCallback(async () => {
    if (!project || selectedList.length === 0) return;
    const msg = selectedList.length === 1
      ? `Delete ${selectedList[0]!.classId} #${selectedList[0]!._id}?`
      : `Delete ${selectedList.length} cabinets?`;
    if (!window.confirm(msg)) return;
    await persist(removeCabinets(project, new Set(selectedList.map((c) => c._id))));
    setSelectedIds(new Set());
  }, [project, selectedList, persist]);

  /** Persist drag-end deltas from the scene gizmo. Reject the drop if
   *  it would overlap another cabinet — invalidating the query forces
   *  React to re-render at the cabinet's last persisted position. */
  const handleSceneMove = useCallback(async (cabId: Id, changes: Partial<Cabinet>) => {
    if (!project) return;
    const next = cabinetRows.map((c) => c._id === cabId ? { ...c, ...changes } : c);
    if (collisionIds(next).has(cabId)) {
      void qc.invalidateQueries({ queryKey: ["obj", "projects", project._id] });
      return;
    }
    await persist(updateCabinet(project, cabId, changes));
  }, [project, cabinetRows, persist, qc]);

  /** Duplicate every selected cabinet, offset by its own width along +X.
   *  If the default offset overlaps an existing cabinet, walk further along
   *  +X (in 6" steps) until a free slot is found. */
  const handleDuplicate = useCallback(async () => {
    if (!project || selectedList.length === 0) return;
    // Track newly-placed cabinets in-flight so two duplicates in the same
    // batch don't choose the same slot. Group new cabinets by their
    // parent garnitur so we keep them in the same run as their source.
    const pending: Cabinet[] = [...cabinetRows];
    const byGarnitur = new Map<Id, Omit<Cabinet, "_id">[]>();
    for (const c of selectedList) {
      const baseX = c.position[0] + c.width + 2;
      let placeX = baseX;
      for (let dx = 0; dx <= 240; dx += 6) {
        const probe: Cabinet = {
          ...c, _id: -1 as Id,
          position: [baseX + dx, c.position[1], c.position[2]],
        };
        if (!collisionIds([...pending, probe]).has(-1 as Id)) {
          placeX = baseX + dx;
          break;
        }
      }
      const dup: Omit<Cabinet, "_id"> = {
        classId:   c.classId,
        position:  [placeX, c.position[1], c.position[2]],
        rotation:  c.rotation,
        anchor:    c.anchor,
        width:     c.width,
        height:    c.height,
        depth:     c.depth,
        overrides: c.overrides,
        cached:    c.cached ?? ({ partCount: 0 } as Cabinet["cached"]),
      };
      pending.push({ ...dup, _id: -1 as Id });
      // Find the source's parent garnitur — first match wins.
      const parentGarnitur = (project.garniturs ?? []).find(
        (g) => (g.cabinets ?? []).some((x) => x._id === c._id),
      );
      const gid = parentGarnitur?._id ?? garniturList[0]?._id;
      if (gid == null) continue;
      const arr = byGarnitur.get(gid) ?? [];
      arr.push(dup);
      byGarnitur.set(gid, arr);
    }
    let next = project;
    const newIds: Id[] = [];
    for (const [gid, drafts] of byGarnitur) {
      const res = addCabinets(next, gid, drafts);
      next = res.project;
      newIds.push(...res.cabinetIds);
    }
    await persist(next);
    setSelectedIds(new Set(newIds));
  }, [project, selectedList, cabinetRows, garniturList, persist]);

  /** Rotate every selected cabinet 90° around Y. Skip rotations that would
   *  send the cabinet into another's footprint — the rest still rotate. */
  const handleRotate90 = useCallback(async () => {
    if (!project || selectedList.length === 0) return;
    let working = cabinetRows;
    const accepted = new Set<Id>();
    for (const c of selectedList) {
      const rotated: Partial<Cabinet> = { rotation: (c.rotation || 0) + Math.PI / 2 };
      const candidate = working.map((x) => x._id === c._id ? { ...x, ...rotated } : x);
      if (collisionIds(candidate).has(c._id)) continue;
      working = candidate;
      accepted.add(c._id);
    }
    if (accepted.size === 0) return;
    const next = updateCabinetsWhere(project, (c) =>
      accepted.has(c._id) ? { rotation: (c.rotation || 0) + Math.PI / 2 } : null,
    );
    await persist(next);
  }, [project, selectedList, cabinetRows, persist]);

  /** Arrow-key nudge: ±1" world XZ (or ±6" with Shift). Applies to every
   *  selected cabinet that wouldn't overlap another after the move. */
  const handleNudge = useCallback(async (dx: number, dz: number) => {
    if (!project || selectedList.length === 0) return;
    let working = cabinetRows;
    const accepted = new Map<Id, [number, number, number]>();
    for (const c of selectedList) {
      const nextPos: [number, number, number] = [
        c.position[0] + dx,
        c.position[1],
        c.position[2] + dz,
      ];
      const candidate = working.map((x) => x._id === c._id ? { ...x, position: nextPos } : x);
      if (collisionIds(candidate).has(c._id)) continue;
      working = candidate;
      accepted.set(c._id, nextPos);
    }
    if (accepted.size === 0) return;
    const next = updateCabinetsWhere(project, (c) => {
      const pos = accepted.get(c._id);
      return pos ? { position: pos } : null;
    });
    await persist(next);
  }, [project, selectedList, cabinetRows, persist]);

  // ── Keyboard shortcuts ───────────────────────────────────────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;

      if (e.key === "Escape") {
        setSelectedIds(new Set());
        return;
      }
      if (selectedList.length === 0) return;

      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        void handleDelete();
      } else if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        void handleRotate90();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === "d" || e.key === "D")) {
        e.preventDefault();
        void handleDuplicate();
      } else if (e.key.startsWith("Arrow")) {
        e.preventDefault();
        const step = e.shiftKey ? 6 : 1;
        if (e.key === "ArrowLeft")  void handleNudge(-step, 0);
        if (e.key === "ArrowRight") void handleNudge(+step, 0);
        if (e.key === "ArrowUp")    void handleNudge(0, -step);
        if (e.key === "ArrowDown")  void handleNudge(0, +step);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedList, handleDelete, handleRotate90, handleDuplicate, handleNudge]);

  if (!id || Number.isNaN(Number(id))) return <div className="empty-state">Invalid project id.</div>;
  if (pLoading) return <div className="empty-state">Loading project…</div>;
  if (pError) {
    return (
      <div className="empty-state">
        <div>Couldn't load project.</div>
        <div className="muted" style={{ marginTop: 8 }}>{(pError as Error).message}</div>
        <button style={{ marginTop: 12 }} onClick={() => nav("/projects")}>Back</button>
      </div>
    );
  }
  if (!project) {
    return (
      <div className="empty-state">
        <div>Project not found.</div>
        <button style={{ marginTop: 12 }} onClick={() => nav("/projects")}>Back</button>
      </div>
    );
  }

  return (
    <div style={{
      display: "grid",
      gridTemplateRows: "auto 1fr",
      height: "100%",
      minHeight: 0,
    }}>
      <ProjectToolbar
        project={project}
        cabinetCount={cabinetRows.length}
        orphanCount={orphanCabinets.length}
        renderAdvance={(p, onChanged) => <StatusAdvance project={p} onChanged={onChanged} />}
      />
    <div style={{
      display: "grid",
      gridTemplateColumns: `${leftCollapsed ? "0px" : "240px"} 1fr ${rightCollapsed ? "0px" : "320px"}`,
      minHeight: 0,
    }}>
      <div style={{ overflow: "hidden", minWidth: 0, display: "flex", flexDirection: "column", height: "100%" }}>
        <CabinetPalette
          projectId={project._id}
          onAdd={handleAdd}
          disabled={false}
        />
        <ProjectOutliner
          projectId={project._id}
          garniturs={garniturList}
          selectedIds={selectedIds}
          onSelectCabinet={handleSelect}
          onMoveCabinet={async (cabinetId, targetGarniturId) => {
            await persist(moveCabinetToGarnitur(project, cabinetId, targetGarniturId));
          }}
          catalog={catalog ?? undefined}
          onSelectPart={handleSelectPart}
          onSelectDrawer={handleSelectDrawer}
          inspectorTarget={secondary}
        />
      </div>

      <div style={{ position: "relative" }}>
        <CollapseToggle
          side="left"
          collapsed={leftCollapsed}
          onToggle={() => setLeftCollapsed(!leftCollapsed)}
        />
        <CollapseToggle
          side="right"
          collapsed={rightCollapsed}
          onToggle={() => setRightCollapsed(!rightCollapsed)}
        />
        <RoomScene
          project={project}
          cabinets={cabinetRows}
          selectedIds={selectedIds}
          collisions={collisions}
          outOfBounds={outOfBounds}
          swingBlocks={swingBlocks}
          onSelect={handleSelect}
          onMove={handleSceneMove}
          pbr={pbr}
        />
        <SceneOverlay
          pbr={pbr}
          onTogglePbr={() => setPbr(!pbr)}
          hasSelection={selectedList.length > 0}
          selectionCount={selectedList.length}
          onRotate={handleRotate90}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
        />
        {orphanCabinets.length > 0 && (
          <OrphanBanner
            count={orphanCabinets.length}
            onDelete={async () => {
              if (!project) return;
              const msg = `Delete ${orphanCabinets.length} cabinet${orphanCabinets.length === 1 ? "" : "s"} with unknown class? This can't be undone.`;
              if (!window.confirm(msg)) return;
              await persist(removeCabinets(project, new Set(orphanCabinets.map((c) => c._id))));
            }}
          />
        )}
      </div>

      <div style={{ overflow: "hidden", minWidth: 0, display: "grid" }}>
      <div style={{ borderLeft: "1px solid var(--line)", display: "flex", flexDirection: "column", width: 320, height: "100%", minHeight: 0 }}>
        <GarnitursPanel
          garniturs={garniturList}
          cabinetCounts={cabinetCountsByGarnitur}
          currentId={currentGarniturId}
          onSelect={setCurrentGarniturId}
          onAdd={handleAddGarnitur}
          onDelete={handleDeleteGarnitur}
        />
        <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          {(() => {
            // Inspector priority:
            //   1. multiple cabinets selected → multi-select panel
            //   2. secondary target (drawer / part) on the focused cabinet
            //   3. single cabinet selected → CabinetEditor
            //   4. nothing → CabinetEditor's empty state ("click a cabinet")
            if (selectedList.length > 1) {
              return (
                <MultiSelectionPanel
                  count={selectedList.length}
                  classCounts={selectedList.reduce<Record<string, number>>((acc, c) => {
                    acc[c.classId] = (acc[c.classId] ?? 0) + 1;
                    return acc;
                  }, {})}
                  onRotate={handleRotate90}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                  onClear={() => setSelectedIds(new Set())}
                />
              );
            }
            const focusedCab = selected;
            if (focusedCab && secondary && secondary.cabinetId === focusedCab._id) {
              if (secondary.kind === "drawer") {
                const drawer = focusedCab.drawers?.find((d) => d._id === secondary.drawerId);
                if (drawer) {
                  return (
                    <DrawerInspector
                      cabinet={focusedCab}
                      drawer={drawer}
                      onSelectCabinet={(id) => { setSecondary(null); setSelectedIds(new Set([id])); }}
                      onChange={(patch) => {
                        const drawers = (focusedCab.drawers ?? []).map((d) =>
                          d._id === drawer._id ? { ...d, ...patch } : d,
                        );
                        handleChange({ drawers });
                      }}
                      onDelete={() => {
                        const drawers = (focusedCab.drawers ?? []).filter((d) => d._id !== drawer._id);
                        handleChange({ drawers: drawers.length ? drawers : undefined });
                        setSecondary(null);
                      }}
                    />
                  );
                }
              } else if (secondary.kind === "part" && catalog) {
                return (
                  <PartInspector
                    cabinet={focusedCab}
                    partId={secondary.partId}
                    catalog={catalog}
                    edgeBands={edgeBands ?? []}
                    onSelectCabinet={(id) => { setSecondary(null); setSelectedIds(new Set([id])); }}
                    onPartOverride={(partId, patch) => {
                      const cur = focusedCab.partOverrides ?? {};
                      const next = { ...cur, [partId]: { ...cur[partId], ...patch } };
                      handleChange({ partOverrides: next });
                    }}
                    onOpenPartEditor={() => openPartEditorModal(focusedCab._id, secondary.partId)}
                  />
                );
              }
            }
            return (
              <CabinetEditor
                cabinet={selected}
                garniturs={garniturList}
                currentGarniturId={selectedGarniturId}
                onMoveToGarnitur={handleMoveSelectedToGarnitur}
                catalog={catalog ?? undefined}
                edgeBands={edgeBands ?? []}
                onChange={handleChange}
                onDelete={handleDelete}
                onRequestPartEditor={(partId) => {
                  if (!selected) return;
                  openPartEditorModal(selected._id, partId);
                }}
              />
            );
          })()}
        </div>
      </div>
      </div>
    </div>
    {editPartCtx && catalog && (() => {
      const cab = cabinetRows.find((c) => c._id === editPartCtx.cabinetId);
      if (!cab || !project) return null;
      return (
        <PartEditor
          cabinet={cab}
          catalog={catalog}
          edgeBands={edgeBands ?? []}
          onClose={() => setEditPartCtx(null)}
          onChange={(partOverrides) => {
            void persist(updateCabinet(project, cab._id, { partOverrides }));
          }}
          initialPartId={editPartCtx.partId ?? null}
        />
      );
    })()}
    </div>
  );
}

function OrphanBanner({ count, onDelete }: { count: number; onDelete: () => void }) {
  return (
    <div
      role="alert"
      style={{
        position: "absolute",
        top: 10, left: "50%", transform: "translateX(-50%)",
        zIndex: 10,
        display: "flex", alignItems: "center", gap: 10,
        background: "rgba(196, 110, 60, 0.92)",
        color: "#fff",
        padding: "6px 12px",
        borderRadius: 6,
        fontSize: 12,
        maxWidth: "80%",
      }}
    >
      <span>
        ⚠ {count} cabinet{count === 1 ? "" : "s"} hidden — unknown class.
        Stale data from an earlier schema or a bad import.
      </span>
      <button
        className="ghost"
        style={{ padding: "3px 10px", fontSize: 11 }}
        onClick={onDelete}
      >
        Delete all
      </button>
    </div>
  );
}

interface CollapseToggleProps {
  side: "left" | "right";
  collapsed: boolean;
  onToggle: () => void;
}

function CollapseToggle({ side, collapsed, onToggle }: CollapseToggleProps) {
  // Chevron points in the direction the panel will move on click:
  // open → click hides → arrow points outward; collapsed → click shows → arrow points inward.
  const chevron =
    (side === "left" && !collapsed) || (side === "right" && collapsed) ? "‹" :
    "›";
  const title =
    `${collapsed ? "Show" : "Hide"} ${side} panel`;
  return (
    <button
      onClick={onToggle}
      title={title}
      aria-label={title}
      style={{
        position: "absolute",
        top: "50%",
        [side]: 0,
        transform: "translateY(-50%)",
        zIndex: 11,
        width: 18,
        height: 56,
        padding: 0,
        display: "grid",
        placeItems: "center",
        background: "rgba(31, 41, 51, 0.85)",
        border: "1px solid var(--line)",
        [side === "left" ? "borderLeft" : "borderRight"]: "none",
        borderRadius: side === "left" ? "0 6px 6px 0" : "6px 0 0 6px",
        color: "var(--fg-dim)",
        fontSize: 14,
        lineHeight: 1,
        cursor: "pointer",
      }}
    >
      {chevron}
    </button>
  );
}

interface SceneOverlayProps {
  pbr: boolean;
  onTogglePbr: () => void;
  hasSelection: boolean;
  selectionCount: number;
  onRotate: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function SceneOverlay({
  pbr, onTogglePbr,
  hasSelection, selectionCount,
  onRotate, onDuplicate, onDelete,
}: SceneOverlayProps) {
  return (
    <>
      <div style={{
        position: "absolute", top: 10, right: 10, zIndex: 10,
        display: "flex", gap: 6, alignItems: "center",
        background: "rgba(31, 41, 51, 0.85)", padding: "4px 8px",
        borderRadius: 6, fontSize: 11, color: "var(--fg-dim)",
      }}>
        <span>Lighting</span>
        <button
          className="ghost"
          style={{ padding: "2px 10px", fontSize: 11 }}
          onClick={onTogglePbr}
          title="Toggle photorealistic preview"
        >
          {pbr ? "PBR" : "Studio"}
        </button>
      </div>

      {hasSelection && (
        <div style={{
          position: "absolute", bottom: 10, left: "50%", transform: "translateX(-50%)",
          zIndex: 10,
          display: "flex", gap: 6, alignItems: "center",
          background: "rgba(31, 41, 51, 0.92)", padding: "6px 10px",
          borderRadius: 6,
        }}>
          <span className="muted" style={{ fontSize: 11, marginRight: 6 }}>
            {selectionCount === 1 ? "Cabinet" : `${selectionCount} cabinets`}
          </span>
          <button className="ghost" style={{ padding: "3px 10px", fontSize: 11 }} onClick={onRotate} title="R">↻ 90°</button>
          <button className="ghost" style={{ padding: "3px 10px", fontSize: 11 }} onClick={onDuplicate} title="Ctrl+D">⧉ Duplicate</button>
          <button className="ghost" style={{ padding: "3px 10px", fontSize: 11, color: "var(--danger)" }} onClick={onDelete} title="Del">✕ Delete</button>
          <span className="muted" style={{ fontSize: 10, marginLeft: 6 }}>Shift+click · R · Ctrl+D · Del · Esc · ←↑↓→</span>
        </div>
      )}
    </>
  );
}

interface MultiPanelProps {
  count: number;
  classCounts: Record<string, number>;
  onRotate: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onClear: () => void;
}

function MultiSelectionPanel({
  count, classCounts, onRotate, onDuplicate, onDelete, onClear,
}: MultiPanelProps) {
  return (
    <aside style={{ borderLeft: "1px solid var(--line)", padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <div className="label">MULTI-SELECTION</div>
        <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>{count} cabinets</div>
      </div>
      <div className="card" style={{ padding: 8 }}>
        {Object.entries(classCounts).map(([cls, n]) => (
          <div key={cls} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "2px 0" }}>
            <span>{cls}</span>
            <span className="muted">×{n}</span>
          </div>
        ))}
      </div>
      <div className="muted" style={{ fontSize: 11 }}>
        Batch ops apply to all selected. Use Shift+click to add/remove.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <button className="ghost" onClick={onRotate}>↻ Rotate 90° all (R)</button>
        <button className="ghost" onClick={onDuplicate}>⧉ Duplicate all (Ctrl+D)</button>
        <button
          className="ghost"
          style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
          onClick={onDelete}
        >✕ Delete all (Del)</button>
      </div>
      <button className="ghost" onClick={onClear}>Clear selection (Esc)</button>
    </aside>
  );
}

function StatusAdvance({ project, onChanged }: { project: Project; onChanged: () => void }) {
  const next = NEXT_STATUS[project.status] ?? [];
  if (next.length === 0) return null;
  async function advance(to: ProjectStatus) {
    await coll("projects").update<Project>(project._id, { status: to });
    onChanged();
  }
  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
      <div className="muted" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Advance to</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {next.map((s) => (
          <button
            key={s}
            className="ghost"
            style={{ fontSize: 11, padding: "3px 8px" }}
            onClick={() => advance(s)}
          >
            {s.replace("_", " ")}
          </button>
        ))}
      </div>
    </div>
  );
}
